import storage from "@/lib/storage";
import type { AuthProviderProps, AuthState } from "@/types/auth";
import api from "@shared/api/client";
import type { User } from "@shared/types/models";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext<AuthState | undefined>(undefined);

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    api.setStorage(storage);
    api.onAuthFailure(() => setUser(null));

    api.auth
      .me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const navigate = useNavigate();

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.auth.loginJwt(email, password);
      setUser(response.user);
      if (response.user.hasSetupComplete) navigate("/dashboard", { replace: true });
      else navigate("/setup", { replace: true });
    },
    [navigate],
  );

  const register = useCallback(async (email: string, password: string, name: string) => {
    const user = await api.auth.register(email, password, name);
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch (err) {
      console.warn("Logout request failed", err);
    } finally {
      setUser(null);
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await api.auth.me();
      setUser(freshUser);
      return freshUser;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(() => ({ user, isLoading, login, register, logout, refreshUser }), [user, isLoading, login, register, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
